import React, { useCallback, useEffect, useState, useRef, useMemo } from 'react';
import { Box, CircularProgress, IconButton, List, ListItem, ListItemText, Drawer, useTheme, useMediaQuery, Collapse, Typography, Button } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import MenuIcon from '@mui/icons-material/Menu';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import api from '../../api';
import BookTutor from '../../workspace/Books/BookTutor';
import ReaderTutorPanel from '../../workspace/Books/ReaderTutorPanel';
import { placeIn, revealSource, selectionIn } from '../../workspace/Books/bookReader';
import { TOC_WIDTH, readTocOpen, saveTocOpen } from '../../workspace/Books/readerToc';

// KaTeX CDN URLs
const KATEX_CSS_URL = 'https://cdn.jsdelivr.net/npm/katex@0.16.9/dist/katex.min.css';
const KATEX_JS_URL = 'https://cdn.jsdelivr.net/npm/katex@0.16.9/dist/katex.min.js';
const KATEX_AUTO_RENDER_URL = 'https://cdn.jsdelivr.net/npm/katex@0.16.9/dist/contrib/auto-render.min.js';

const HTMLContentViewer = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const contentRef = useRef(null);
  const paneRef = useRef(null);

  const [htmlContent, setHtmlContent] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [tableOfContents, setTableOfContents] = useState([]);
  // The contents bar: a sheet on phones; on wide screens collapsible, and remembered.
  const [drawerOpen, setDrawerOpen] = useState(() => (isMobile ? false : readTocOpen()));
  const toggleToc = () => setDrawerOpen((cur) => { if (!isMobile) saveTocOpen(!cur); return !cur; });
  const tocShown = drawerOpen && !isMobile;
  const [katexLoaded, setKatexLoaded] = useState(false);
  const [topicName, setTopicName] = useState('Reading Material');
  const [expandedSections, setExpandedSections] = useState({});
  const [noContent, setNoContent] = useState(false);
  // Ask AI: the template module the chapters belong to (the book tutor's key),
  // and the passage the student last selected.
  const [moduleId, setModuleId] = useState(null);
  const [quote, setQuote] = useState(null);

  const quoteText = useRef('');
  const onSelect = useCallback(() => {
    const picked = selectionIn(contentRef.current);
    // The same passage again (mouseup and selectionchange both report it) is not a new one.
    if (picked && picked.text !== quoteText.current) {
      quoteText.current = picked.text;
      setQuote(picked);
    }
  }, []);
  const clearQuote = useCallback(() => { quoteText.current = ''; setQuote(null); }, []);
  // Phones finish a selection after the touch ends: catch it once it settles.
  useEffect(() => {
    let timer;
    const changed = () => { clearTimeout(timer); timer = setTimeout(onSelect, 600); };
    document.addEventListener('selectionchange', changed);
    return () => { clearTimeout(timer); document.removeEventListener('selectionchange', changed); };
  }, [onSelect]);
  const place = useCallback(() => placeIn(contentRef.current, paneRef.current), []);
  const onSource = useCallback((source) => {
    if (revealSource(contentRef.current, source) && isMobile) setDrawerOpen(false);
  }, [isMobile]);

  // Group TOC items hierarchically - h1 items contain their child h2/h3 items
  const groupedToc = useMemo(() => {
    const groups = [];
    let currentGroup = null;

    tableOfContents.forEach((item) => {
      if (item.level === 1) {
        currentGroup = { ...item, children: [] };
        groups.push(currentGroup);
      } else if (currentGroup) {
        currentGroup.children.push(item);
      } else {
        // h2/h3 without a parent h1 - create a standalone group
        groups.push({ ...item, children: [] });
      }
    });

    return groups;
  }, [tableOfContents]);

  const toggleSection = (id) => {
    setExpandedSections((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const extractTableOfContents = (content) => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content;

    const headings = tempDiv.querySelectorAll('h1, h2, h3');
    const toc = Array.from(headings).map((heading, index) => {
      const id = `heading-${index}`;
      heading.id = id;
      return {
        id,
        text: heading.textContent,
        level: parseInt(heading.tagName[1]),
      };
    });

    return { toc, processedContent: tempDiv.innerHTML };
  };

  const scrollToHeading = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      if (isMobile) setDrawerOpen(false);
    }
  };

  // Load KaTeX from CDN
  useEffect(() => {
    const loadKatex = async () => {
      if (window.renderMathInElement) {
        setKatexLoaded(true);
        return;
      }

      if (!document.querySelector(`link[href="${KATEX_CSS_URL}"]`)) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = KATEX_CSS_URL;
        document.head.appendChild(link);
      }

      await new Promise((resolve, reject) => {
        if (window.katex) {
          resolve();
          return;
        }
        const script = document.createElement('script');
        script.src = KATEX_JS_URL;
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });

      await new Promise((resolve, reject) => {
        if (window.renderMathInElement) {
          resolve();
          return;
        }
        const script = document.createElement('script');
        script.src = KATEX_AUTO_RENDER_URL;
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });

      setKatexLoaded(true);
    };

    loadKatex().catch(err => console.error('Failed to load KaTeX:', err));
  }, []);

  // Render math after content is loaded and KaTeX is ready
  useEffect(() => {
    if (htmlContent && katexLoaded && contentRef.current && window.renderMathInElement) {
      try {
        window.renderMathInElement(contentRef.current, {
          delimiters: [
            { left: '$$', right: '$$', display: true },
            { left: '$', right: '$', display: false },
            { left: '\\(', right: '\\)', display: false },
            { left: '\\[', right: '\\]', display: true },
          ],
          throwOnError: false,
          trust: true,
        });
      } catch (err) {
        console.error('KaTeX rendering failed:', err);
      }
    }
  }, [htmlContent, katexLoaded]);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const { reading_material_url, topic_template_id, topic_id } = location.state || {};

        // Determine API endpoint based on mode
        let chaptersApiUrl = null;
        if (topic_template_id) {
          // Cohort mode - use template chapters API
          chaptersApiUrl = `api/reading-material/topic/${topic_template_id}/chapters/`;
        } else if (topic_id) {
          // 1:1 mode - use course topic chapters API
          chaptersApiUrl = `api/reading-material/course-topic/${topic_id}/chapters/`;
        }

        // Fetch chapters if we have an API URL
        if (chaptersApiUrl) {
          const chaptersResponse = await api.get(chaptersApiUrl);
          const { chapters, topic_name, module_id } = chaptersResponse.data;
          if (module_id) setModuleId(module_id);

          if (topic_name) {
            setTopicName(topic_name);
          }

          if (!chapters || chapters.length === 0) {
            setNoContent(true);
            setIsLoading(false);
            return;
          }

          // Fetch all chapter contents and combine them
          const chapterContents = await Promise.all(
            chapters.map(async (chapter, position) => {
              if (!chapter.production_url) return null;

              try {
                const response = await axios.get(chapter.production_url);
                let data = response.data;

                // Get base URL for relative paths
                const urlWithoutQuery = chapter.production_url.split('?')[0];
                const baseUrl = urlWithoutQuery.substring(0, urlWithoutQuery.lastIndexOf('/') + 1);

                // Rewrite relative URLs
                data = data.replace(/src=["'](?!https?:\/\/|data:|\/\/)([^"']+)["']/gi, (match, path) => {
                  return `src="${baseUrl}${path}"`;
                });
                data = data.replace(/href=["'](?!https?:\/\/|data:|\/\/|#|mailto:)([^"']+)["']/gi, (match, path) => {
                  return `href="${baseUrl}${path}"`;
                });
                data = data.replace(/url\(["']?(?!https?:\/\/|data:|\/\/)([^"')]+)["']?\)/gi, (match, path) => {
                  return `url("${baseUrl}${path}")`;
                });

                // Extract just the body content if it's a full HTML document
                const bodyMatch = data.match(/<body[^>]*>([\s\S]*)<\/body>/i);
                const content = bodyMatch ? bodyMatch[1] : data;

                return {
                  title: chapter.title,
                  content: content,
                  // Its place in the module: what the tutor calls this chapter.
                  position,
                };
              } catch (err) {
                console.error(`Failed to fetch chapter ${chapter.title}:`, err);
                return null;
              }
            })
          );

          // Combine all chapters with titles as section headers
          const validChapters = chapterContents.filter(Boolean);
          const combinedHtml = validChapters.map((chapter, index) => {
            return `
              <section class="chapter-section" id="chapter-${index}" data-chapter="${chapter.position}">
                <h1 class="chapter-title">${chapter.title}</h1>
                ${chapter.content}
              </section>
              ${index < validChapters.length - 1 ? '<hr class="chapter-divider" style="margin: 40px 0; border: none; border-top: 2px solid #e0e0e0;" />' : ''}
            `;
          }).join('\n');

          const { toc, processedContent } = extractTableOfContents(combinedHtml);
          setTableOfContents(toc);
          setHtmlContent(processedContent);
        }
        // Fallback: single reading material URL (legacy support)
        else if (reading_material_url) {
          const response = await axios.get(reading_material_url);
          let data = response.data;

          const urlWithoutQuery = reading_material_url.split('?')[0];
          const baseUrl = urlWithoutQuery.substring(0, urlWithoutQuery.lastIndexOf('/') + 1);

          data = data.replace(/src=["'](?!https?:\/\/|data:|\/\/)([^"']+)["']/gi, (match, path) => {
            return `src="${baseUrl}${path}"`;
          });
          data = data.replace(/href=["'](?!https?:\/\/|data:|\/\/|#|mailto:)([^"']+)["']/gi, (match, path) => {
            return `href="${baseUrl}${path}"`;
          });
          data = data.replace(/url\(["']?(?!https?:\/\/|data:|\/\/)([^"')]+)["']?\)/gi, (match, path) => {
            return `url("${baseUrl}${path}")`;
          });

          const { toc, processedContent } = extractTableOfContents(data);
          setTableOfContents(toc);
          setHtmlContent(processedContent);
        } else {
          setNoContent(true);
          setIsLoading(false);
          return;
        }
      } catch (error) {
        console.error('Error fetching content:', error);
        setNoContent(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchContent();
  }, [location.state, navigate]);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (noContent) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        sx={{ backgroundColor: '#f5f5f5', p: 3 }}
      >
        <MenuBookIcon sx={{ fontSize: 80, color: '#bdbdbd', mb: 2 }} />
        <Typography variant="h5" sx={{ color: '#424242', mb: 1, fontWeight: 500 }}>
          No Reading Material Available
        </Typography>
        <Typography variant="body1" sx={{ color: '#757575', mb: 3, textAlign: 'center', maxWidth: 400 }}>
          Reading material has not been added for this topic yet. Please check back later or contact your mentor.
        </Typography>
        <Button
          variant="contained"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{
            backgroundColor: '#1976d2',
            '&:hover': { backgroundColor: '#1565c0' },
            textTransform: 'none',
            px: 3,
            py: 1,
          }}
        >
          Go Back
        </Button>
      </Box>
    );
  }

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <Box sx={{
      height: '100vh',
      backgroundColor: '#fff',
      display: 'flex',
      overflow: 'hidden',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0
    }}>
      {(isMobile || drawerOpen) && (
      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{
          width: TOC_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: TOC_WIDTH,
            boxSizing: 'border-box',
            top: '56px',
            height: 'calc(100% - 56px)',
            borderRight: '1px solid #dadce0',
            borderTop: '1px solid #dadce0',
          },
        }}
      >
        <List sx={{
          py: 0,
          overflowY: 'auto',
          height: '100%',
          '&::-webkit-scrollbar': {
            display: 'none'
          },
          msOverflowStyle: 'none',
          scrollbarWidth: 'none'
        }}>
          {groupedToc.map((group) => (
            <React.Fragment key={group.id}>
              <ListItem
                button
                sx={{
                  pl: 1,
                  py: 0.5,
                  '&:hover': {
                    backgroundColor: 'rgba(95,99,104,0.08)',
                  },
                }}
              >
                {group.children.length > 0 && (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleSection(group.id);
                    }}
                    sx={{ p: 0.25, mr: 0.5 }}
                  >
                    {expandedSections[group.id] ? (
                      <ExpandMoreIcon fontSize="small" />
                    ) : (
                      <ChevronRightIcon fontSize="small" />
                    )}
                  </IconButton>
                )}
                <ListItemText
                  primary={group.text}
                  onClick={() => scrollToHeading(group.id)}
                  sx={{
                    ml: group.children.length === 0 ? 3.5 : 0,
                    cursor: 'pointer',
                    '& .MuiListItemText-primary': {
                      fontSize: group.level === 1 ? '1rem' : '0.9rem',
                      fontWeight: group.level === 1 ? 500 : 400,
                      color: group.level === 1 ? '#333' : '#666',
                    }
                  }}
                />
              </ListItem>
              {group.children.length > 0 && (
                <Collapse in={expandedSections[group.id]} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {group.children.map((child) => (
                      <ListItem
                        key={child.id}
                        button
                        onClick={() => scrollToHeading(child.id)}
                        sx={{
                          pl: child.level === 2 ? 5 : 7,
                          py: 0.25,
                          '&:hover': {
                            backgroundColor: 'rgba(95,99,104,0.08)',
                          },
                        }}
                      >
                        <ListItemText
                          primary={child.text}
                          sx={{
                            '& .MuiListItemText-primary': {
                              fontSize: '0.85rem',
                              fontWeight: 400,
                              color: '#666',
                            }
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              )}
            </React.Fragment>
          ))}
        </List>
      </Drawer>
      )}

      <Box sx={{
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        width: tocShown ? `calc(100% - ${TOC_WIDTH}px)` : '100%'
      }}>
        <Box sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          backgroundColor: '#ffffff',
          color: '#5f6368',
          padding: '8px 16px',
          display: 'flex',
          alignItems: 'center',
          boxShadow: '0 1px 2px 0 rgba(60,64,67,0.3), 0 2px 6px 2px rgba(60,64,67,0.15)',
          zIndex: 1000,
          height: '56px',
          boxSizing: 'border-box'
        }}>
          <IconButton
            onClick={handleBack}
            sx={{
              color: '#5f6368',
              mr: 2,
              width: '40px',
              height: '40px',
              border: '1px solid #dadce0',
              borderRadius: '50%',
              backgroundColor: '#f1f3f4',
              '&:hover': {
                backgroundColor: '#e8eaed',
                boxShadow: '0 1px 2px 0 rgba(60,64,67,0.3), 0 1px 3px 1px rgba(60,64,67,0.15)'
              }
            }}
          >
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <IconButton
            onClick={toggleToc}
            aria-label={drawerOpen ? 'Hide contents' : 'Show contents'}
            title={drawerOpen ? 'Hide contents' : 'Show contents'}
            sx={{
              color: '#5f6368',
              mr: 2,
              width: '40px',
              height: '40px',
              border: '1px solid #dadce0',
              borderRadius: '50%',
              backgroundColor: drawerOpen ? '#e8f1f6' : '#f1f3f4',
              '&:hover': { backgroundColor: '#e8eaed' }
            }}
          >
            {drawerOpen ? <MenuOpenIcon fontSize="small" /> : <MenuIcon fontSize="small" />}
          </IconButton>
          <Box component="h1" sx={{
            fontSize: '1.125rem',
            fontWeight: 400,
            m: 0,
            flexGrow: 1,
            color: '#202124',
            fontFamily: 'Google Sans, Roboto, Arial, sans-serif'
          }}>
            {topicName}
          </Box>
        </Box>

        <Box ref={paneRef} sx={{
          p: 0,
          pt: '72px',
          width: '100%',
          pl: 3,
          pr: moduleId && !isMobile ? 3 : 0,
          transition: 'margin-left 0.3s',
          position: 'relative',
          overflowY: 'auto',
          height: '100vh',
          '&::-webkit-scrollbar': {
            width: '8px',
            height: '8px'
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1'
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#c1c1c1',
            borderRadius: '4px',
            '&:hover': {
              background: '#a8a8a8'
            }
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '1px',
            backgroundColor: '#dadce0'
          },
          '& img': { maxWidth: '100%', height: 'auto' },
          '& h1': { color: '#333', marginTop: '0.5em', fontSize: '2rem' },
          '& h2': { color: '#333', marginTop: '1em', fontSize: '1.5rem' },
          '& h3': { color: '#333', marginTop: '1em', fontSize: '1.25rem' },
          '& p': { lineHeight: 1.6, color: '#444' },
          '& code': { backgroundColor: '#f5f5f5', padding: '2px 4px', borderRadius: '4px' },
          '& .chapter-title': {
            color: '#0172A6',
            borderBottom: '2px solid #0172A6',
            paddingBottom: '8px',
            marginBottom: '24px'
          }
        }}>
          <div ref={contentRef} onMouseUp={onSelect} onKeyUp={onSelect}
            dangerouslySetInnerHTML={{ __html: htmlContent }} />
        </Box>
      </Box>
      {moduleId && (
        <ReaderTutorPanel mobile={isMobile} openSignal={quote}>
          <BookTutor moduleId={moduleId} place={place} quote={quote} onClearQuote={clearQuote}
            onSource={onSource} />
        </ReaderTutorPanel>
      )}
    </Box>
  );
};

export default HTMLContentViewer;
